/**
 * Browser DOM-related utilities.
 *
 * IMPORTANT: These utilities must be safe to import in non-browser environments
 * (SSR / node) — they should guard against missing `window`/`document`.
 */
import { isDevelopment, getEnvType, EnvTypes } from './environment.utils';
const isBrowserEnvironment = () => typeof window !== 'undefined';
export const setCookie = ({ cookieName, cookieValue, expirationDays = 365, domain }) => {
    if (!isBrowserEnvironment()) {
        return;
    }
    try {
        const date = new Date();
        date.setTime(date.getTime() + expirationDays * 24 * 60 * 60 * 1000);
        const expires = `expires=${date.toUTCString()}`;
        let cookie = `${cookieName}=${encodeURIComponent(cookieValue)};${expires};path=/;`;
        if (domain) {
            cookie += `domain=${domain};`;
        }
        // Add SameSite for CSRF protection
        cookie += 'SameSite=Lax;';
        document.cookie = cookie;
    }
    catch (error) {
        // In production, this would be tracked by the app's error service
        // eslint-disable-next-line no-restricted-syntax
        console.error('Failed to set cookie', { cookieName, domain, error });
    }
};
export const getCookie = (cookieName) => {
    if (typeof document === 'undefined' || !cookieName) {
        return null;
    }
    let match;
    try {
        match = document.cookie
            ?.split(';')
            .map(part => part.trim())
            .find(part => part.startsWith(`${cookieName}=`));
    }
    catch (error) {
        // A sandboxed iframe without allow-same-origin throws on document.cookie
        // eslint-disable-next-line no-restricted-syntax
        console.error('Failed to read cookie', { cookieName, error });
        return null;
    }
    if (!match) {
        return null;
    }
    const value = match.substring(cookieName.length + 1);
    try {
        return decodeURIComponent(value);
    }
    catch {
        return value;
    }
};
export function convertMtaCookieToHeader() {
    const mta_uuid = getCookie('hb_mta_uuid');
    if (mta_uuid) {
        return { 'HB-Api-Mta-Uuid': mta_uuid };
    }
    return {};
}
export const scrollLayoutTo = (options) => {
    const scrollToOptions = {
        top: 0,
        behavior: 'smooth',
        ...options
    };
    (document.querySelector('.main-ui-view') ?? window)?.scrollTo(scrollToOptions);
};
const isPublicTemplate = location.pathname.includes('/template/view') ||
    location.pathname.includes('/template-preview');
export function getCookieDomain() {
    if (isDevelopment()) {
        return 'localhost';
    }
    if (getEnvType() === EnvTypes.Staging) {
        return '.hbdev.io';
    }
    return isPublicTemplate ? '.hbportal.co' : '.honeybook.com';
}
export function isValidTitle(value) {
    return !!value && value !== 'null' && value !== 'undefined';
}
export function setTitleAndFavicon(title, faviconUrl) {
    document.title = isValidTitle(title) ? title : '';
    if (faviconUrl) {
        const link = document.querySelector("link[rel*='icon']") ||
            document.createElement('link');
        link.type = 'image/x-icon';
        link.rel = 'shortcut icon';
        link.href = faviconUrl;
        document.getElementsByTagName('head')[0].appendChild(link);
    }
}
//# sourceMappingURL=dom.utils.js.map